import { formatUSDwithCents } from 'helpers/money.helper';
import { useState } from 'react';
import {
  getPlanIndex,
  isPremium,
  isPremiumPaymentPending,
} from 'helpers/userHelper';
import { useSelector, useDispatch } from 'react-redux';
import { useComplexState } from 'hook/helper.hook';
import { useHistory } from 'react-router-dom';
import {
  addSesameTurbo1WeekFreePlanToPrecheckout,
  addSesameTurbo1WeekFreePlanToCheckout,
  addCROAPlanToPrecheckout,
  addCROAPlanToCheckout,
  addPublicPremiumTurboFreePlanToCheckout,
  addTurbo3BGating1WeekFreePlanToCheckout,
  addTurbo3BGating1WeekFreePlanToCheckoutTU,
  addNewPremiumPackage,
  addPremiumPlusPackage,
  addPublicPremiumPremiumPlusPlanToCheckout,
  addPremiumMultiStackedToCheckout,
  dispatchPremiumMultiStackedToCheckout,
} from 'actions/checkout.actions';
import {
  getNextRecurringDayOfMonth,
  getCancelSubscriptionDate,
  getCancelSubscriptionForTrial,
  getSimplifiedDate,
  getDiffDaysBetweenDates,
} from 'helpers/dateHelper';
import { reportOptimizely } from 'externals/_tracking/optimizely';
import {
  CLICK,
  VIEW_POPUP,
} from 'externals/_tracking/types/eventTypes.constants';
import { postCancelPremiumSubscription } from 'externals/_services/userSubscription.premium.service';
import { postCouponRedeem } from 'externals/_services/userSubscription.premium.service';
import { useLogger } from 'hook/error.hook';
import { getUserInfo } from 'actions/userInfo.actions';
import { PLATINUM_FREE_TRIAL } from 'types/checkout.constants';
import { wrapService } from 'actions/service_wrapper.actions';
import $t from 'locales/en/translation';
import { openNewTab } from 'helpers/domHelper';
import {
  RENT_REPORTING_VERTICAL,
  SUBSCRIPTION_VERTICAL,
} from 'types/mrph.vertical.constants';
import { SAVE_SALE_RENT_REPORTING } from 'types/mrph.module.constants';
import { useFlag } from 'hook/flag.hook';
import { isVariationLoadedAndEnabled } from 'helpers/optimizely.helper';
import { isBillingError } from 'helpers/billingHelper';
import {
  MAKE_PAYMENT,
  PAYMENT_SUCCESS as CLAIM_OFFER_SUCCESS,
  PAYMENT_FAILURE as CLAIM_OFFER_FAILURE,
} from 'types/claimOffer.constants';
import { onPremiumConversion } from 'actions/premium.actions';
import { emptyObj } from 'helpers/objectHelpers';
import {
  SUBSCRIPTION_CYCLE_ANNUALLY,
  SUBSCRIPTION_CYCLE_MONTHLY,
  SUBSCRIPTION_CYCLE_SEMI_ANNUALLY,
} from '../types/premium.constants';
import { getCoupons } from 'externals/_services/user.service';

const a_postCancelPremiumSubscription = wrapService(
  postCancelPremiumSubscription,
  {
    name: 'postCancelPremiumSubscription',
  },
);

const a_postCouponRedeem = wrapService(postCouponRedeem, {
  name: 'postCouponRedeem',
});

export const premiumCancellationReasons = () => {
  const premiumCancelReasonsFinal = [];
  const reasons = $t.premium['cancelReasons'];
  Object.keys(reasons).forEach(function (key) {
    premiumCancelReasonsFinal.push(reasons[key]);
  });
  return premiumCancelReasonsFinal;
};

export const usePremiumHelpers = eventHandler => {
  const { userInfo } = useSelector(state => ({
    userInfo: state.userInfo.userInfo,
    config: state.config.config,
  }));
  const [isChangePopupVisible, setIsChangePopupVisible] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const [processCancellation, setProcessCancellation] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [
    isCancelPremiumTrialPopupVisible,
    setIsCancelPremiumTrialPopupVisible,
  ] = useState(false);
  const trialPeriodUsed = userInfo ? userInfo.trialPeriodUsed : null;
  const [cancelReason, setCancelReason] = useState(null);
  const [disableCancelReasonButton, setDisableCancelReasonButton] =
    useState(true);
  const [premiumDeactivateState, setPremiumDeactivateState] = useComplexState({
    disableClaimOfferButton: null,
    popupVisible: null,
    showCancelSaveTheSaleContent: null,
    showReasonOptions: null,
    showCongratsStay: null,
    showCongratsStayCoupon: null,
    showSadToSeeYouGo: null,
    showCouponOffer: null,
    showBillingForm: null,
  });

  const [couponsState, setCouponState] = useComplexState({
    couponsLoading: false,
    hasCoupons: false,
    couponsUsed: '',
  });

  const logger = useLogger({
    name: 'premium.hook',
    categories: [`premium`],
  });

  const billingPeriodMonths = userInfo?.premiumPlanInfo?.billingPeriodMonths;
  const buildPlanPricing = () => {
    const m = billingPeriodMonths > 1 ? billingPeriodMonths + ' months' : 'mo';
    if (userInfo?.premiumPlanInfo?.price) {
      return formatUSDwithCents(userInfo?.premiumPlanInfo?.price) + ' / ' + m;
    } else {
      return '--.--';
    }
  };

  const getSubscriptionCycle = () => {
    let subscriptionText = '';
    const trialPeriodText = userInfo?.premiumPlanInfo?.freeTrialPeriod
      ? '(7 - Day Free Trial)'
      : '';

    if (billingPeriodMonths === 1) {
      subscriptionText = SUBSCRIPTION_CYCLE_MONTHLY;
    } else if (billingPeriodMonths === 6) {
      subscriptionText = SUBSCRIPTION_CYCLE_SEMI_ANNUALLY;
    } else if (billingPeriodMonths === 12) {
      subscriptionText = SUBSCRIPTION_CYCLE_ANNUALLY;
    }
    return subscriptionText + ' ' + trialPeriodText;
  };

  const planPrice = buildPlanPricing();

  const isClaimOffer = history?.location?.state === '/claimOffer/login';

  const isPremiumUser = isPremium(userInfo);
  const currentPremiumPlan = getPlanIndex(userInfo);

  const planPricing =
    formatUSDwithCents(userInfo?.premiumPlanInfo?.price) ?? '--.--';
  const freeTrialRemains = getDiffDaysBetweenDates(
    userInfo?.premiumPlanInfo?.trialEndDate,
    new Date(),
  );
  const subscriptionRemains = getDiffDaysBetweenDates(
    userInfo?.premiumPlanInfo?.nextBillingDate,
    new Date(),
  );
  const daysRemaining = userInfo?.premiumPlanInfo?.freeTrialPeriod
    ? `Free trial ends in ${freeTrialRemains} days`
    : `${subscriptionRemains} days remain`;

  const subscriptionBillingCycle = getSubscriptionCycle();

  const currentBillingPeriod = `${getSimplifiedDate(
    userInfo?.premiumPlanInfo?.periodStartDate ??
      userInfo?.premiumPlanInfo?.enrolledDate,
  )} - ${getSimplifiedDate(userInfo?.premiumPlanInfo?.nextBillingDate)}`;
  const enrollmentDate = getSimplifiedDate(
    userInfo?.premiumPlanInfo?.enrolledDate,
  );
  const nextBillingDate =
    userInfo && isPremiumUser
      ? getNextRecurringDayOfMonth(userInfo.premiumPlanInfo?.enrolledDate)
      : null;

  const handleCancelReasonClick = e => {
    setCancelReason(e.target.value);
    setDisableCancelReasonButton(false);
  };

  const redirectedToClaimOffer = (step, errorMessage = '') => {
    let search = `?step=${step}`;
    if (errorMessage) {
      search += `&error=${errorMessage}`;
    }
    history.push({
      pathname: '/claimOffer/convert',
      search: search,
      state: '/claimOffer/login',
    });
  };

  const finalizeCancelPremiumSubscription = (
    cancellationDate,
    answer,
    comment,
    author,
  ) => {
    return new Promise((resolve, reject) => {
      dispatch(
        a_postCancelPremiumSubscription(
          cancellationDate,
          answer,
          comment,
          author,
        ),
      )
        .then(res => dispatch(getUserInfo(true)))
        .then(() => {
          eventHandler(CLICK, {
            'Click Type': 'Submit',
            'Pop Up Name': 'Premium Cancel - Survey',
            'Cancellation Reason': `Reason ${cancelReason} - ${
              $t.premium['cancelReasons'][`reason${cancelReason}`]
            }`,
            Vertical: RENT_REPORTING_VERTICAL,
          });
          setProcessCancellation(false);
          setPremiumDeactivateState({
            showCancelSaveTheSaleContent: false,
            showCongratsStay: false,
            showCongratsStayCoupon: false,
            showCouponOffer: false,
            showReasonOptions: false,
            showSadToSeeYouGo: true,
          });
          resolve();
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  const isEligibleForRedeemCoupon =
    isPremiumUser && !userInfo?.premiumPlanInfo?.cancelDate;

  const redeemCoupon = couponCode => {
    if (!isEligibleForRedeemCoupon) {
      logger.info('User found in redeem coupon flow when invalid');
    }

    return new Promise((resolve, reject) => {
      dispatch(a_postCouponRedeem(couponCode))
        .then(res => dispatch(getUserInfo(true)))
        .then(res => dispatch(onPremiumConversion(res)))
        .then(() => {
          eventHandler(VIEW_POPUP, {
            'Pop Up Name': 'Premium Cancel - Coupon Applied',
            Vertical: RENT_REPORTING_VERTICAL,
            'Module Name': SAVE_SALE_RENT_REPORTING,
          });
          if (isClaimOffer) {
            redirectedToClaimOffer(CLAIM_OFFER_SUCCESS);
            return;
          }
          setPremiumDeactivateState({
            showCancelSaveTheSaleContent: false,
            showReasonOptions: false,
            showCongratsStay: false,
            showCongratsStayCoupon: true,
            showSadToSeeYouGo: false,
            showCouponOffer: false,
            showBillingForm: false,
          });
          resolve();
        })
        .catch(err => {
          if (isClaimOffer) {
            if (isBillingError(err.code)) {
              redirectedToClaimOffer(MAKE_PAYMENT);
            } else {
              const errorMessage = typeof err === 'object' ? err.message : err;
              redirectedToClaimOffer(CLAIM_OFFER_FAILURE, errorMessage);
            }
          } else {
            logger.showError('Unable to process this transaction', err);
          }
          reject(err);
          setPremiumDeactivateState({
            disableClaimOfferButton: false,
          });
          logger.reportAPIError(err);
        });
    });
  };

  const goToOverview = modalName => {
    eventHandler(CLICK, {
      'Click Type': 'Go To Overview',
      'Pop Up Name': `Premium Cancel - ${modalName}`,
      Vertical: RENT_REPORTING_VERTICAL,
    });
    history.push('/dashboard');
  };

  const handleShowCongratsStay = modalName => {
    setPremiumDeactivateState({
      showCancelSaveTheSaleContent: false,
      showCongratsStay: true,
      showCouponOffer: false,
      showReasonOptions: false,
      showSadToSeeYouGo: false,
    });
    eventHandler(CLICK, {
      'Click Type': 'Keep Premium',
      'Pop Up Name': `Premium Cancel - ${modalName}`,
      Vertical: RENT_REPORTING_VERTICAL,
    });
    eventHandler(VIEW_POPUP, {
      'Pop Up Name': 'Premium Cancel - Congrats Stay',
      Vertical: RENT_REPORTING_VERTICAL,
      'Module Name': SAVE_SALE_RENT_REPORTING,
    });
  };

  const handleShowCouponOffer = () => {
    setPremiumDeactivateState({
      showCancelSaveTheSaleContent: false,
      showCongratsStay: false,
      showCongratsStayCoupon: false,
      showCouponOffer: true,
      showReasonOptions: false,
      showSadToSeeYouGo: false,
    });
    eventHandler(CLICK, {
      'Click Type': 'Submit',
      'Pop Up Name': 'Premium Cancel - Survey',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    eventHandler(VIEW_POPUP, {
      'Pop Up Name': 'Premium Cancel - Coupon',
      Vertical: RENT_REPORTING_VERTICAL,
      'Module Name': SAVE_SALE_RENT_REPORTING,
    });
  };

  const handleHideCongratsStay = () => {
    setPremiumDeactivateState({
      showCongratsStay: false,
    });
  };

  const handleShowSadToSeeYouGo = () => {
    eventHandler(VIEW_POPUP, {
      'Pop Up Name': 'Premium Cancel - Sad To See You Go',
      Vertical: RENT_REPORTING_VERTICAL,
      'Module Name': SAVE_SALE_RENT_REPORTING,
    });
    handleCancelPremiumSubmit();
  };

  const handleClaimOffer = couponCode => {
    setPremiumDeactivateState({
      disableClaimOfferButton: true,
    });
    eventHandler(CLICK, {
      'Click Type': 'Claim Offer',
      'Pop Up Name': 'Premium Cancel - Coupon',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    if (isPremiumPaymentPending(userInfo)) {
      setPremiumDeactivateState({
        showCouponOffer: false,
        showBillingForm: true,
      });
    } else {
      redeemCoupon(couponCode);
    }
  };

  const handleShowReasonOptions = () => {
    setPremiumDeactivateState({
      disableClaimOfferButton: false,
      showCancelSaveTheSaleContent: false,
      showReasonOptions: true,
    });
    eventHandler(CLICK, {
      'Click Type': 'Deactivate Premium',
      'Pop Up Name': 'Premium Cancel - Are you sure',
      Vertical: RENT_REPORTING_VERTICAL,
    });
    eventHandler(VIEW_POPUP, {
      'Pop Up Name': 'Premium Cancel - Survey',
      Vertical: RENT_REPORTING_VERTICAL,
      'Module Name': SAVE_SALE_RENT_REPORTING,
    });
  };

  const handleHideReasonOptions = () => {
    setPremiumDeactivateState({
      showReasonOptions: false,
    });
  };

  const handleShowCancelSaveTheSaleContent = () => {
    setPremiumDeactivateState({
      popupVisible: true,
      showCancelSaveTheSaleContent: true,
    });
  };

  const handleCancelPremiumSubmit = e => {
    const isFreeTrial =
      userInfo.premiumPlanInfo.serviceId &&
      userInfo.premiumPlanInfo.serviceId === PLATINUM_FREE_TRIAL &&
      userInfo.premiumPlanInfo.freeTrialPeriod
        ? true
        : false;
    const premiumSubscriptionType = isFreeTrial ? 'Free Trial ' : '';
    const defaultComment = `Cancel Premium ${premiumSubscriptionType} Subscription`;
    var cancellationDate = userInfo.premiumPlanInfo.freeTrialPeriod
      ? getCancelSubscriptionForTrial(userInfo)
      : getCancelSubscriptionDate(userInfo);
    setProcessCancellation(true);
    setErrorMessage(null);
    var answer = `Done by user`;
    var author = `${userInfo.firstName} ${userInfo.lastName}`;

    finalizeCancelPremiumSubscription(
      cancellationDate,
      answer,
      defaultComment,
      author,
    ).catch(err => {
      setProcessCancellation(false);
      logger.reportAPIError(err);
      logger.showError(
        'Unable to process this transaction',
        'Please try again in few minutes',
      );
      logger.errorException(
        'Unable to process cancel premium subscription',
        err,
      );
    });
  };

  const onClickSesameTurbo1WeekFree = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      dispatch(addSesameTurbo1WeekFreePlanToCheckout(history));
    }
  };

  const onClickTurbo3BGating1WeekFree = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      dispatch(addTurbo3BGating1WeekFreePlanToCheckout(history));
    }
  };

  const toggleChangePopup = () => {
    setIsChangePopupVisible(!isChangePopupVisible);
  };

  const onClickEventPublicPremium = (
    eventName,
    planName,
    pageName,
    planType,
    moduleName,
  ) => {
    trackClickEvent(eventName, pageName, planType, planName, moduleName);
    onClickPublicPremium(planName);
  };

  const onClickPublicPremium = planName => {
    onClickPublicPremiumTurboFreePlanCheckout(planName);
  };

  const onClickPublicPremiumTurboFreePlanCheckout = planName => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      reportOptimizely('clickPublicPremiumTurboFreePlanCheckoutButton');
      if (planName === 'WEB_PP_3BGATING_7_DAY_FREE_TRIAL_TU') {
        dispatch(addPublicPremiumPremiumPlusPlanToCheckout(history));
      } else {
        dispatch(addPublicPremiumTurboFreePlanToCheckout(history));
      }
    }
  };

  const onClickCROACheckout = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      reportOptimizely('clickCROACheckoutButton');
      dispatch(addCROAPlanToCheckout(history));
    }
  };

  const onClickCROAPrecheckout = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      reportOptimizely('clickCROAPrecheckoutButton');
      dispatch(addCROAPlanToPrecheckout(history));
    }
  };

  const onClickSesameTurbo1WeekFreePrecheckout = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      reportOptimizely('clickPremiumSesameTurbo1WeekFreePrecheckoutButton');
      dispatch(addSesameTurbo1WeekFreePlanToPrecheckout(history));
    }
  };

  const onClickPremium = planName => {
    if (planName === '3BGating') {
      onClickTurbo3BGating1WeekFree();
    } else {
      onClickSesameTurbo1WeekFree();
    }
  };

  const trackClickEvent = (
    finalEventName,
    pageName,
    planType,
    planName,
    moduleName,
    vertical,
  ) => {
    let productOfferAttribute = {};
    let initialAttributes = {
      'Click Type': finalEventName,
      'Page Name': pageName,
      'Plan Type': planType,
      'Module Name': moduleName,
    };
    if (planName === 'SesameTurbo2MonthsFree') {
      productOfferAttribute = {
        'Product Offer': 'Turbo 2 Month Free Tiral',
      };
    }

    if (vertical) {
      initialAttributes.Vertical = vertical;
    }

    eventHandler(
      CLICK,
      Object.assign({}, initialAttributes, productOfferAttribute),
    );
  };

  const onClickEventPrecheckout = (planName, pageName) => {
    trackClickEvent(planName, pageName);
  };

  const onClickMultiPanelPremium = (
    clickTypeName,
    planName,
    pageName,
    planType,
    moduleName,
  ) => {
    trackClickEvent(
      `${clickTypeName} CTA`,
      pageName,
      planType,
      planName,
      moduleName,
      SUBSCRIPTION_VERTICAL,
    );
    if (planName === 'WEB_PP_3BGATING_7_DAY_FREE_TRIAL') {
      onClickPremiumPlusPlan();
    } else if (planName === 'WEB_999FT_4M3B') {
      onClickNewPremiumPlan();
    }
  };

  const onClickNewPremiumPlan = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      dispatch(addNewPremiumPackage(history));
    }
  };

  const onClickPremiumPlusPlan = () => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      dispatch(addPremiumPlusPackage(history));
    }
  };

  const onClickEventPremium = (
    clickTypeName,
    planName,
    pageName,
    planType,
    moduleName,
  ) => {
    trackClickEvent(
      `${clickTypeName} CTA`,
      pageName,
      planType,
      planName,
      moduleName,
      SUBSCRIPTION_VERTICAL,
    );
    onClickPremium(planName.split(' ')[0]);
  };

  const onRulesClick = () => {
    openNewTab('https://www.creditsesame.com/premium-5k-sweepstakes/');
  };

  const onClickPremiumMultiStacked = planCode => {
    if (isPremiumUser) {
      setIsChangePopupVisible(true);
    } else {
      reportOptimizely('clickPremiumMultiStackedButton');
      dispatch(addPremiumMultiStackedToCheckout(planCode, history));
    }
  };

  const onClickDispatchPremiumMultiStacked = planCode => {
    dispatch(dispatchPremiumMultiStackedToCheckout(planCode));
  };

  const onClickEventPremiumMultiStacked = planCode => {
    trackClickEvent(
      'Premium Multi Stacked CTA',
      'Checkout',
      planCode,
      planCode,
      'Free Trail Turbo',
      SUBSCRIPTION_VERTICAL,
    );
    onClickPremiumMultiStacked(planCode);
  };

  const buildComparisonFeatures = optimzielyConfigComparisonChart => {
    if (emptyObj(optimzielyConfigComparisonChart)) {
      return null;
    }
    const { features, plans } = optimzielyConfigComparisonChart;
    let planFeatures = [];

    features?.forEach((featureDetail, featureIndex) => {
      let featureInfo = {
        title: featureDetail.title,
      };
      let planFeatureInfo = [];
      plans?.forEach((planDetail, planIndex) => {
        if (planDetail?.features?.includes(featureDetail.id)) {
          planFeatureInfo.push({
            checkColor: planDetail?.feature_check_color,
            isCheck: true,
          });
        } else {
          planFeatureInfo.push({
            checkColor: planDetail?.feature_check_color,
            isCheck: false,
          });
        }
        featureInfo.plans = planFeatureInfo;
      });
      planFeatures.push(featureInfo);
    });

    return {
      headers: plans,
      features: planFeatures,
    };
  };

  const getCouponsInfo = () => {
    setCouponState({
      couponsLoading: true,
    });
    getCoupons()
      .then(res => {
        if (res.length > 0) {
          const activeCoupons = res?.filter(
            coupon => coupon?.couponStatus === 'ACTIVE',
          );
          setCouponState({
            loading: false,
            hasCoupons: activeCoupons?.length > 0,
            couponsUsed: activeCoupons,
          });
        } else {
          setCouponState({
            loading: false,
          });
        }
      })
      .catch(() => {
        setCouponState({
          loading: false,
        });
      });
  };

  const premiumHelper = {
    cancelReason,
    currentBillingPeriod,
    currentPlanIndex: currentPremiumPlan,
    currentPremiumPlan,
    daysRemaining,
    disableCancelReasonButton,
    enrollmentDate,
    errorMessage,
    goToOverview,
    handleCancelPremiumSubmit,
    handleCancelReasonClick,
    handleClaimOffer,
    handleHideCongratsStay,
    handleHideReasonOptions,
    handleShowCancelSaveTheSaleContent,
    handleShowCongratsStay,
    handleShowCouponOffer,
    handleShowReasonOptions,
    handleShowSadToSeeYouGo,
    onClickMultiPanelPremium,
    isCancelPremiumTrialPopupVisible,
    isChangePopupVisible,
    isPremiumUser,
    nextBillingDate,
    onClickCROACheckout,
    onClickCROAPrecheckout,
    onClickEventPremiumMultiStacked,
    onClickDispatchPremiumMultiStacked,
    onClickEventPrecheckout,
    onClickEventPremium,
    onClickEventPublicPremium,
    onClickPublicPremiumTurboFreePlanCheckout,
    onClickSesameTurbo1WeekFreePrecheckout,
    onClickTurbo3BGating1WeekFree,
    onRulesClick,
    planPricing,
    premiumCancellationReasons,
    premiumDeactivateState,
    processCancellation,
    redeemCoupon,
    redirectedToClaimOffer,
    setCancelReason,
    setDisableCancelReasonButton,
    setIsCancelPremiumTrialPopupVisible,
    subscriptionBillingCycle,
    toggleChangePopup,
    trialPeriodUsed,
    hasFreeTrialBeenUsedPrior: userInfo?.trialPeriodUsed,
    showFreeTrial: !userInfo?.trialPeriodUsed,
    buildComparisonFeatures,
    planPrice,
    couponsState,
    getCouponsInfo,
  };

  return premiumHelper;
};
